import { useRef, useState, useCallback, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { hapticMedium } from '../../utils/haptics';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void> | void;
}

const THRESHOLD = 60;
const MAX_PULL = 100;

export function PullToRefresh({ children, onRefresh }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const pullingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (refreshing) return;
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) return;
    startYRef.current = e.touches[0].clientY;
    pullingRef.current = true;
  }, [refreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pullingRef.current || refreshing) return;
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) {
      pullingRef.current = false;
      setPullDistance(0);
      return;
    }
    const deltaY = e.touches[0].clientY - startYRef.current;
    if (deltaY < 0) {
      setPullDistance(0);
      return;
    }
    // Dampen the pull (logarithmic feel)
    const dampened = Math.min(MAX_PULL, deltaY * 0.4);
    setPullDistance(dampened);
  }, [refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pullingRef.current) return;
    pullingRef.current = false;

    if (pullDistance >= THRESHOLD) {
      hapticMedium();
      setRefreshing(true);
      setPullDistance(THRESHOLD * 0.6);
      await onRefresh();
      setRefreshing(false);
    }
    setPullDistance(0);
  }, [pullDistance, onRefresh]);

  const isActivated = pullDistance >= THRESHOLD;

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto min-h-0 relative"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className="flex items-center justify-center overflow-hidden transition-[height] duration-150 ease-out"
        style={{ height: pullDistance > 0 || refreshing ? `${pullDistance}px` : 0 }}
      >
        <div
          className={`transition-transform duration-150 ${isActivated || refreshing ? 'text-accent' : 'text-slate-500'}`}
          style={{
            transform: `rotate(${refreshing ? 0 : Math.min(pullDistance / THRESHOLD, 1) * 180}deg)`,
          }}
        >
          <Loader2
            size={20}
            className={refreshing ? 'animate-spin' : ''}
          />
        </div>
      </div>

      {children}
    </div>
  );
}
